import * as React from 'react';

import classNames from 'classnames';
import tinytime from 'tinytime';

import { Text } from '@components/common';
import { BulletIconSVG } from '@components/svgs/BulletIconSVG';

const template = tinytime('{MMMM} {DD} at {h}:{mm}{a}');

type SignedConsentFormsProps = {
  consentForms: SignedConsentForm[];
  variant?: 'slideout' | 'full';
};

function renderConsentFormItem(consentForm: SignedConsentForm, index: number, variant: 'slideout' | 'full') {
  // Define classes based on variant
  const isFull = variant === 'full';

  const filenameClass = classNames('mb-2 flex-1 font-normal text-gray-700', {
    'text-sm': isFull,
    'text-base': !isFull
  });

  const dateClass = classNames({
    'text-xs': isFull,
    'text-sm': !isFull
  });

  const projectClass = classNames('mt-2', {
    'text-xs': isFull,
    'text-sm': !isFull
  });

  const pdfLinkClass = classNames(
    'h400 ml-4 flex-shrink-0 cursor-pointer self-center hover:underline text-indigo-600',
    {
      'text-base': !isFull
    }
  );

  const pdfNotAvailableClass = classNames('h400 ml-4 flex-shrink-0 self-center text-gray-400', {
    'text-base': !isFull
  });

  return (
    <React.Fragment key={index}>
      {index > 0 && <hr className='my-2 border-gray-200' />}
      <div className='flex flex-row items-center justify-between'>
        <div className='flex min-w-0 flex-1 flex-col'>
          <h3 className={filenameClass}>{consentForm.filename}</h3>
          <Text color='gray-500' h='400' className={dateClass}>
            {isFull ? (
              <div className='flex items-center gap-x-2'>
                {new Date(consentForm.signed_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
                <BulletIconSVG />
                <span>Signed</span>
              </div>
            ) : (
              <span>Signed {template.render(new Date(consentForm.signed_at))}</span>
            )}
          </Text>
          {consentForm.project_title && (
            <Text h='400' color='gray-500' className={projectClass}>
              {consentForm.project_id ? (
                <a href={`/studies/${consentForm.project_id}`} className='text-indigo-600 hover:underline'>
                  {consentForm.project_title}
                </a>
              ) : (
                consentForm.project_title
              )}
            </Text>
          )}
        </div>
        {consentForm.pdf_url ? (
          <a className={pdfLinkClass} target='_blank' href={consentForm.pdf_url} rel='noopener noreferrer'>
            Download PDF
          </a>
        ) : (
          <span className={pdfNotAvailableClass}>PDF not available</span>
        )}
      </div>
    </React.Fragment>
  );
}

export const SignedConsentForms: React.FC<SignedConsentFormsProps> = ({ consentForms, variant = 'slideout' }) => {
  if (!consentForms || consentForms.length === 0) return null;

  return (
    <div className={'flex flex-col gap-y-1'}>
      {consentForms.map((consentForm, index) => renderConsentFormItem(consentForm, index, variant))}
    </div>
  );
};
